/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import AttendanceWidget from './attendance-widget';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tagName } from '@ember-decorators/component';
import { rejectBy } from '@linc-technologies/linced-common/utils/arrays';

@tagName('')
export default class AttendanceUnexplained extends AttendanceWidget {
  @service
  attendanceCodes;

  @computed('attendanceCodes.allCodes.@each.{quick,old}')
  get quickCodes() {
    return rejectBy(
      this.attendanceCodes.allCodes.filter((code) => code.quick),
      'old',
    );
  }

  @computed('attendanceCodes.allCodes.@each.{quick,old}')
  get otherCodes() {
    return rejectBy(
      this.attendanceCodes.allCodes.filter((code) => !code.quick),
      'old',
    );
  }

  @tracked
  allDay = false;

  @action
  setAllDay(value) {
    this.allDay = value;
  }

  @computed(
    'record.person.learner.primaryContacts.@each.{receivesSms,phoneMobile}',
  )
  get canSendSms() {
    let primaryContacts = this.get('record.person.learner.primaryContacts');
    // We've had this explode for attendance records for learners that are not loaded
    // This is because we only have current students loaded
    // But if they're not current we probably shouldn't be sending attendance SMSs
    if (!isEmpty(primaryContacts)) {
      return this.get('record.person.learner.primaryContacts').some(
        (contact) => {
          // Requires the SMS flag to be enabled, and a non-empty mobile number.
          return contact.receivesSms && !isEmpty(contact.phoneMobile);
        },
      );
    }
    return false;
  }

  dateObj = null;

  @alias('dateObj.date')
  date;

  @tracked
  person;

  @tracked
  records;

  @tracked
  newCode;

  @tracked
  reason;

  @action
  setReason(value) {
    this.reason = value;
  }

  @tracked
  effectiveFrom;

  @action
  setEffectiveFrom(value) {
    this.effectiveFrom = value;
  }

  @tracked
  effectiveFromDefaulted;

  @tracked
  reasonOpen;

  onSetAllDay() {}
  openConflictDialog() {}
  openNoteConflictDialog() {}
  launchLateArrival() {}

  @action
  setStatus(status) {
    if (!this.allDay) {
      // Reload the record first, in case somebody else has already added
      // notes; otherwise, we might unintentionally wipe the notes.
      let setWidgetStatus = super.setStatus.bind(this);
      let previousCode = this.record.code;
      let previousNotes = this.record.notes;
      if (!isEmpty(this.record.changedAttributes().notes)) {
        previousNotes = this.record.changedAttributes().notes[0];
      }
      let newNotes = this.record.notes;
      this.record.reload().then((record) => {
        record.rollbackAttributes();
        if (record.code === status.code) {
          // Somebody else set the same code we're trying to.
          if (previousNotes !== newNotes && newNotes !== record.notes) {
            // We have an unsaved note; raise a conflict warning.
            this.openNoteConflictDialog(record, newNotes);
          } else {
            // Declare success and move on.
            this.toaster.success('Attendance data saved.');
          }
        } else if (
          record.code !== previousCode &&
          !this.emptyOrUnknown(record.code)
        ) {
          // The code has been changed to a non-empty, non-unknown code.
          this.openConflictDialog(record, previousCode, status.code);

          if (previousNotes !== newNotes && newNotes !== record.notes) {
            // We have an unsaved note; raise a conflict warning.
            this.openNoteConflictDialog(record, newNotes);
          }
        } else {
          if (newNotes !== previousNotes) {
            // We've changed the notes.
            if (record.notes === previousNotes) {
              // Nobody else changed the notes; push ours back in.
              this.record.set('notes', newNotes);
            } else if (record.notes !== newNotes) {
              // Notes were changed (to something different) by someone else.
              this.openNoteConflictDialog(this.record, newNotes);
            }
          }

          // Code is unchanged, or we can change it without an audit reason.
          setWidgetStatus(status);
        }
      });
    } else {
      // Set properties which are needed to create new records.
      this.date = this.record.get('date');
      this.person = this.record.get('person');

      // Fetch the latest records for this person, so that we're up-to-date.
      let filters = {
        dateFrom: this.date,
        dateTo: this.date,
        personId: this.person.get('id'),
      };
      this.store
        .query('attendance/record', filters)
        .then((records) => {
          // Push the latest records back up into the widget.
          this.records = records;
          // Check for a conflicting change.
          let firstPeriod = this.periods[0];
          let firstPeriodRecord = records.find(
            (record) => record.period_id === firstPeriod.get('id'),
          );
          if (
            !isEmpty(firstPeriodRecord) &&
            !this.emptyOrUnknown(firstPeriodRecord.code) &&
            firstPeriodRecord.code !== status.code
          ) {
            // The code has been changed to a non-empty, non-unknown code.
            this.openConflictDialog(firstPeriodRecord, '?', status.code);
            if (!isEmpty(firstPeriodRecord.changedAttributes().notes)) {
              // Also show note dialog if applicable.  Only the first period's
              // note will be updated if the user chooses to use their note.
              let newNotes = firstPeriodRecord.notes;
              firstPeriodRecord.rollbackAttributes();
              this.openNoteConflictDialog(firstPeriodRecord, newNotes);
            }
          } else {
            // Loop over the day's periods and update this student's status for all of them.
            let promises = [];
            this.periods.forEach((period) => {
              let record = records.find(
                (record) => record.period_id === period.get('id'),
              );

              if (record) {
                if (record.code !== status.code) {
                  let effectiveFrom;
                  if (this.trackMovements) {
                    effectiveFrom = record.get('movements.lastObject.time');
                  }
                  promises.push(
                    this.updateRecord(
                      status.code,
                      record.get('notes'),
                      'All day attendance',
                      effectiveFrom,
                      record,
                    ),
                  );
                } else {
                  // Same code, but we might have an unsaved note.
                  if (!isEmpty(record.changedAttributes().notes)) {
                    // Also show note dialog if applicable.  Only the first period's
                    // note will be updated if the user chooses to use their note.
                    let newNotes = record.notes;
                    record.rollbackAttributes();
                    this.openNoteConflictDialog(record, newNotes);
                  }
                }
              } else {
                promises.push(
                  this.createRecord(period, status, this.record.get('notes'))
                    .then((record) => record.save())
                    .then(() => {
                      this.toaster.success('Attendance data saved.');
                    })
                    .catch(() => {
                      this.toaster.error('Error saving attendance data!');
                    }),
                );
              }
            });
            return Promise.all(promises);
          }
        })
        .then(() => {
          // Run onSetAllDay() only after all saves have resolved.
          this.onSetAllDay();
        });
    }
  }

  @action
  saveNotes() {
    let record = this.record;
    let changedNotes = record.changedAttributes().notes;
    let previousNotes = changedNotes ? changedNotes[0] : record.notes;
    let newNotes = record.notes;
    record.reload().then((record) => {
      record.rollbackAttributes();
      if (record.notes === previousNotes) {
        this.updateRecord(record.get('code'), newNotes, 'Added note');
      } else {
        this.openNoteConflictDialog(record, newNotes);
      }
    });
  }

  @action
  handleLaunchLateArrival() {
    this.launchLateArrival(this.record.person, this.record);
  }

  // triggerAudit only happens when falling through to the attendance-widget's
  // setStatus and the code is going from unknown to present.
  triggerAudit(record, status) {
    // effectiveFrom defaults to empty if the record isn't for today,
    // or if it's an NZ MoE school.
    let effectiveFrom = '';
    if (this.trackMovements) {
      // Default to the last movement's start time for now, as only the last
      // movement being unexplained will result in the student's inclusion in
      // the Pending Explanation list.  If there are no movements (which
      // shouldn't happen), fall back to the start of the period.
      // When unexplained partial absences (which aren't the latest movement)
      // are shown, they should use the absence movement's start time instead,
      // so that the partial absence is updated to become present.
      effectiveFrom = record.get('movements.lastObject.timeString');
      if (isEmpty(effectiveFrom)) {
        effectiveFrom = record.get('period.startTime');
      }
    }
    this.newCode = status.code;
    this.reason = '';
    this.effectiveFrom = effectiveFrom;
    this.effectiveFromDefaulted = !isEmpty(effectiveFrom);
    this.reasonOpen = true;
  }
}
